import { useMemo } from 'react';
import { useLocations } from '@/Locations/hooks/use-locations';
import { LocationAd } from '@/Locations/types/locations';
import { isCurrentlyActive } from '@/lib/date-utils';

export interface AdWithLocation {
  ad: LocationAd;
  location: {
    title: string;
    banner: string;
    city: string;
    state: string;
  };
}

export interface AdsGroupedByLocation {
  locationTitle: string;
  locationBanner: string;
  locationCity: string;
  locationState: string;
  ads: LocationAd[];
}

export function useAds() {
  const { data: locations, isLoading, error } = useLocations();

  const adsWithLocations = useMemo<AdWithLocation[]>(() => {
    if (!locations) return [];

    const result: AdWithLocation[] = [];

    locations.forEach((location) => {
      if (!location.ads || location.ads.length === 0) return;

      location.ads
        .filter((ad) => isCurrentlyActive(ad.AdStartDate, ad.AdEndDate))
        .forEach((ad) => {
          result.push({
            ad,
            location: {
              title: location.LocationTitle,
              banner: location.LocationBanner,
              city: location.LocationAddress.LocationAddressCity,
              state: location.LocationAddress.LocationAddressState,
            },
          });
        });
    });

    return result;
  }, [locations]);

  const adsGroupedByLocation = useMemo<AdsGroupedByLocation[]>(() => {
    if (!locations) return [];

    const grouped = new Map<string, AdsGroupedByLocation>();

    locations.forEach((location) => {
      if (!location.ads || location.ads.length === 0) return;

      const activeAds = location.ads.filter((ad) =>
        isCurrentlyActive(ad.AdStartDate, ad.AdEndDate)
      );

      if (activeAds.length === 0) return;

      const key = location.LocationTitle;
      const existing = grouped.get(key);

      if (existing) {
        existing.ads.push(...activeAds);
      } else {
        grouped.set(key, {
          locationTitle: location.LocationTitle,
          locationBanner: location.LocationBanner,
          locationCity: location.LocationAddress.LocationAddressCity,
          locationState: location.LocationAddress.LocationAddressState,
          ads: [...activeAds],
        });
      }
    });

    return Array.from(grouped.values()).sort((a, b) =>
      a.locationTitle.localeCompare(b.locationTitle)
    );
  }, [locations]);

  const totalAdsCount = useMemo(() => {
    return adsWithLocations.length;
  }, [adsWithLocations]);

  return {
    adsWithLocations,
    adsGroupedByLocation,
    totalAdsCount,
    isLoading,
    error,
  };
}
